import { z } from "zod";
import type { EpOnlineResponse } from "./contracts";

export type EnergyLabelView = {
  state: "found" | "not-found" | "ambiguous";
  address: string;
  label: string | null;
  registeredAt: string | null;
  validUntil: string | null;
  checkedAt: string | null;
  stale: boolean;
  expired: boolean;
  indicators: {
    demand: number | null;
    fossil: number | null;
    renewable: number | null;
  };
};

export const energyLabelViewSchema: z.ZodType<EnergyLabelView> = z.object({
  state: z.enum(["found", "not-found", "ambiguous"]),
  address: z.string(),
  label: z.string().nullable(),
  registeredAt: z.string().nullable(),
  validUntil: z.string().nullable(),
  checkedAt: z.string().nullable(),
  stale: z.boolean(),
  expired: z.boolean(),
  indicators: z.object({
    demand: z.number().nullable(),
    fossil: z.number().nullable(),
    renewable: z.number().nullable(),
  }),
});
export const energyFinderResponseSchema = z.discriminatedUnion("success", [
  z.object({
    success: z.literal(true),
    data: energyLabelViewSchema.nullable(),
  }),
  z.object({
    success: z.literal(false),
    error: z.enum(["invalid", "busy", "ambiguous", "unavailable"]),
  }),
]);

export function toEnergyLabelView(row: {
  bagObjectId: string;
  energyLabel: string | null;
  registeredAt: string | null;
  validUntil: string | null;
  assessmentData: EpOnlineResponse | null;
  matchedAddress: { street: string; number: string; postalCode: string };
  lookupStatus: string;
  lastSuccessAt: Date | string | null;
  nextCheckAt: Date | string;
}): EnergyLabelView {
  const matching = (row.assessmentData ?? []).filter((r) =>
    [r.BAGVerblijfsobjectID, r.BAGLigplaatsID, r.BAGStandplaatsID].includes(
      row.bagObjectId,
    ),
  );
  const record = matching.length === 1 ? matching[0] : null;
  const end = row.validUntil
    ? Date.parse(`${row.validUntil.slice(0, 10)}T23:59:59Z`)
    : null;
  return {
    state: row.energyLabel
      ? "found"
      : row.lookupStatus === "ERROR"
        ? "ambiguous"
        : "not-found",
    address: `${row.matchedAddress.street} ${row.matchedAddress.number}, ${row.matchedAddress.postalCode.replace(/^(\d{4})([A-Z]{2})$/, "$1 $2")}`,
    label: row.energyLabel,
    registeredAt: row.registeredAt,
    validUntil: row.validUntil,
    checkedAt: row.lastSuccessAt
      ? new Date(row.lastSuccessAt).toISOString()
      : null,
    stale:
      !!row.energyLabel &&
      (row.lookupStatus === "ERROR" ||
        new Date(row.nextCheckAt).getTime() < Date.now()),
    expired: end !== null && end < Date.now(),
    indicators: {
      demand: record?.Energiebehoefte ?? null,
      fossil: record?.PrimaireFossieleEnergie ?? null,
      renewable: record?.Aandeel_hernieuwbare_energie ?? null,
    },
  };
}
